import { useEffect } from "react";
import { get as lodashGet } from "lodash-es";
import { FormattedMessage, useIntl } from "react-intl";
import { ButtonSizes, Button } from "@certa/catalyst/components/Button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger
} from "@certa/catalyst/components/Dropdown";

import { Language } from "@certa/icons/components/Language";
import { useLanguageStore } from "@certa/common/store/language/useLanguageStore";

import { languageConstants } from "main/src/js/constants";
import { renderLanguageNativeName } from "main/src/js/utils/languageHelpers";
import { NO_SUPPORTED_LANGUAGES, SELECT_LANGUAGE_HEIGHT } from "./constants";
import type { OptionItem } from "@certa/types";
import { useGetSupportedLanguages } from "@certa/common/hooks/useGetSupportedLanguages";

import { removeBracketsAndAppendDash } from "@certa/common/utils/helper";
import { Godaam } from "@certa/common/utils/Godaam";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";
import type { InstanceConfiguration } from "@certa/queries/types/config.types";

type SelectLanguageProps = {
  width?: string;
  showShortLanguage?: boolean;
  onLanguageChange: (value: string) => void;
  onLanguageClick?: (open: boolean) => void;
};

export const SelectLanguage = (props: SelectLanguageProps) => {
  const {
    width,
    showShortLanguage: shouldShowShortLanguage = true,
    onLanguageChange,
    onLanguageClick
  } = props;
  const supportedLanguages = useGetSupportedLanguages();
  const { data: config } = useInstanceConfigQuery();
  const _supportedLanguagesJson = lodashGet(
    config,
    "supported_languages_json",
    {} as InstanceConfiguration["supported_languages_json"]
  );

  const intl = useIntl();

  const preferredLanguage = useLanguageStore(state => state.language);
  const setLanguage = useLanguageStore(state => state.setLanguage);

  useEffect(() => {
    if (!Godaam.preferredLanguage) {
      setLanguage(languageConstants.DEFAULT_LOCALE);
    }
  }, [setLanguage]);

  if (supportedLanguages.length === NO_SUPPORTED_LANGUAGES) return null;

  const renderLanguageMenuTitle = () => {
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        <FormattedMessage id={"loginPageInstances.languageText"} />:{" "}
        <span style={{ textTransform: "uppercase" }}>{preferredLanguage}</span>
      </span>
    );
  };

  const renderLanguageMenuItem = (language: OptionItem) => {
    const translatedLanguage = intl.formatMessage({
      id: `language.${_supportedLanguagesJson[language.value]}`,
      defaultMessage: _supportedLanguagesJson[language.value]
    });
    const nativeLanguageWithBrackets = renderLanguageNativeName(language.value);
    const formattedTranslatedLanguage =
      removeBracketsAndAppendDash(translatedLanguage);
    if (nativeLanguageWithBrackets) {
      const nativeLanguage = removeBracketsAndAppendDash(
        nativeLanguageWithBrackets
      );
      return `${formattedTranslatedLanguage} (${nativeLanguage} - ${language.value})`;
    }
    return `${formattedTranslatedLanguage} (${language.value})`;
  };

  const translatedPreferredLanguage = intl.formatMessage({
    id: `language.${_supportedLanguagesJson[preferredLanguage]}`,
    defaultMessage: _supportedLanguagesJson[preferredLanguage]
  });

  return (
    <DropdownMenu
      key="language-menu"
      onOpenChange={open => onLanguageClick && onLanguageClick(open)}
    >
      <DropdownMenuTrigger>
        <Button
          size={ButtonSizes.SMALL}
          leftIcon={<Language />}
          showDropdownArrow
          data-testid="select-language-button"
        >
          {shouldShowShortLanguage
            ? renderLanguageMenuTitle()
            : translatedPreferredLanguage}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent width={width} height={SELECT_LANGUAGE_HEIGHT}>
        {supportedLanguages.map(language => {
          return (
            <DropdownMenuCheckboxItem
              key={language.value}
              value={language.value}
              checked={preferredLanguage === language.value}
              onChange={(_, value) => onLanguageChange(value)}
            >
              {renderLanguageMenuItem(language)}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
